<template>
	<div id="qpe_line_bar_complex" ref="line_bar_complex">
        <div :id="id" :style="{'text-align': 'center'}"/>
    </div>
</template>

<script>
    import { 
        get_diagram_shape,
        init_chart_resize_observer,
    } from '@/utils/chart/common.js'
    import { 
        get_container_instance,
        draw_background_for_screenshot,
    } from '@/utils/chart/background.js'
    import {
        draw_x_axis_label,
        get_scale_time,
        draw_x_axis_time_tick,
        draw_day_line,
    } from '@/utils/chart/x_axis.js'
    import {
        draw_y_axis_label,
        get_scale_y,
        draw_y_axis_tick,
        draw_y_axis_basic_background,
    } from '@/utils/chart/y_axis.js'
    import { 
        draw_cross,
        draw_triangle,
        draw_triangle_text,
    } from '@/utils/chart/graph.js'
    import { 
        draw_tooltip,
        get_content_instance,
    } from '@/utils/chart/tooltip.js'
    import { judge_value_valid } from '@/utils/chart/verification.js'
    import { 
        transform_datetime_str_to_chart_need_format 
    } from '@/utils/common/custom_time.js'
    import { take_screenshot } from '@/utils/common/screenshot.js'
    import { mapState } from 'vuex'
    import * as d3 from 'd3';

	export default {
        name: 'qpe_line_bar_complex',
                      
        props: {
            id: {
                type: String,
                default() {
                    return 'unique_chart'
                }
            },
            // 原始資料 跟下面那個擇一填
            choosed_time_series_data: {
                type: Array,
                default () {
                    return []
                }
            },
            
            // 選擇測站的時間 跟下面那個擇一個填
            x_axis_time_series_data: {
                type: Array,
                default () {
                    return []
                }
            },
            
            basic_layout_setting: {
                type: Object,
                default () {
                    // margin.left 不能小於 25
                    return {
                        'margin': {
                            'top': 80,
                            'right': 50,
                            'bottom': 45,
                            'left': 30,
                        }
                    }
                }
            },
            // 直方圖主表題 必填
            svg_main_title: {
                type: String,
                default () {
                    return '我是 props 主標題'
                }
            },
            // 直方圖副標題 必填
            svg_sub_title: {
                type: String,
                default () {
                    return '我是 props 副標題'
                }
            },
            // X軸圖說
            // X軸 ticks *不是數字打幾就會出現幾個 tick
            // X軸 display_interval 每幾個 tick 會顯示值
            x_axis: {
                type: Object,
                default () {
                    return {
                        'label': '我是 props X軸圖說',
                        'ticks': 48,
                        'display_interval': 4,
                        'label_distance': 40
                    }
                }
            },
            // y軸圖說
            // y軸 ticks * 不是數字打幾就會出現幾個間隔
            y_axis: {
                type: Object,
                default () {
                    return {
                        'label': '我是 props Y軸圖說',
                        'ticks': 10,
                        'display_interval': 1,
                        'max_value': 100,
                        'min_value': 0,
                        'label_distance': 40
                    }
                }
            },
            // 跨日線
            show_day_line: {
                type: Boolean,
                default () {
                   return true
                }
            },
            // 顏色分階 (灰灰白白那個)
            show_tick_color: {
                type: Boolean,
                default () {
                    return true
                }
            },
            // 標示資料結束時間
            show_data_end_time: {
                type: Boolean,
                default () {
                    return true
                }
            },
            draw_legend_function: {
                type: Function,
                default () {
                    return function() {}
                }
            },

            tooltip_value_text: {
                type: String,
                default() {
                    return this.$t('__chart_common.tooltip_value')
                }
            },

            tooltip_value_unit: {
                type: String,
                default() {
                    return ''
                }
            }
        },

		data() {
			return {
                width: 0,
                height: 0,
                resize_observer: null,
            }
        },
        
		computed: {
            diagram_shape() {
                return get_diagram_shape(this.choosed_time_series_data)
            }
        },

        created() {
            this.$nextTick(() => { 
                this.resize_observer = !this.resize_observer && init_chart_resize_observer.call(this, this.$refs.line_bar_complex)
                this.resize_observer.observe(document.querySelector('.display-block__wrapper'))
            })
        },

        beforeDestroy() {
            this.resize_observer.unobserve(this.$refs.line_bar_complex)
        },

		methods: {
            rerender_svg() {
                d3.selectAll(`#${this.id} > svg`).remove();
                d3.selectAll(`#${this.id} > .tooltip`).remove();
                d3.selectAll(`#${this.id} > .tooltip-line`).remove();
                d3.select(`#${this.id} > .drag_svg`)
                    .on('start', null)
                    .on('drag', null)
                    .on('end', null)
                this.init_pic()
            },

            init_pic() {
                const common_options = {
                    id: this.id, 
                    width: this.width, 
                    height: this.height, 
                    margin: this.basic_layout_setting.margin
                }
                
                // 建立 svg 範圍
                const container = get_container_instance(common_options)
                draw_background_for_screenshot(container, common_options)

                const svg = container
                    .append('g')

                // X軸 圖說 (時間)
                const x_axis_label_options = {
                    ...common_options,
                    x_axis: this.x_axis
                }
                draw_x_axis_label(svg, x_axis_label_options)
                
                // Y軸圖說 (水位)
                const y_axis_label_options = {
                    ...common_options,
                    y_axis: this.y_axis
                }
                draw_y_axis_label(svg, y_axis_label_options)

                const scale_time_options = {
                    ...common_options,
                    start_time_str: this.x_axis_time_series_data[0],
                    end_time_str: this.x_axis_time_series_data.slice(-1)[0],
                }
                const scale_time = get_scale_time(scale_time_options)
                const x_axis_tick_options = {
                    ...common_options,
                    x_axis: this.x_axis,
                    scale_time
                }
                draw_x_axis_time_tick(svg, x_axis_tick_options)

                // Y軸
                const scale_y_options = {
                    ...common_options,
                    ...{ y_axis: this.y_axis }
                }
                const scale_y = get_scale_y(scale_y_options)
                
                const y_axis_tick_options = {
                    ...scale_y_options,
                    ...{
                        scale: scale_y
                    }
                }
                draw_y_axis_tick(svg, y_axis_tick_options)

                this.show_tick_color && draw_y_axis_basic_background(svg, common_options)
                this.show_day_line && draw_day_line(svg, { ...common_options, ...{ scale_time } })
                
                // 內容
                const time_series_data_options = {
                    ...common_options,
                    scale_time,
                    scale_y,
                }
                this.draw_time_series_data(svg, time_series_data_options)
            },

            draw_time_series_data(svg, options) {            
                const { width, height, margin, scale_time, scale_y } = options

                const scale_data = d3.scaleLinear()
                    .domain([0, this.choosed_time_series_data.length - 1])
                    .range([0, width])

                // draw bar
                svg.selectAll('.bar')
                    .data(this.choosed_time_series_data)
                    .enter()
                    .append('rect')
                    .attr('fill', (d) => {
                        return judge_value_valid(d) ? 'steelblue' : 'transparent'
                    })
                    .attr('x', (d, i) => {
                        const basic_x_distance = margin.left + scale_data(i)
                        return i === 0 ? basic_x_distance : basic_x_distance - scale_data(1)/2
                    })
                    // 從上到下
                    .attr('y', (d) => {
                        switch (this.diagram_shape) {
                            case 'down_to_up':
                                return judge_value_valid(d) ? scale_y(d) : scale_y(this.y_axis.min_value + 0.25)
                            case 'up_to_down':
                                return scale_y(d)
                            case 'from_center':
                                if (d > 0) {
                                    return scale_y(d)
                                }else if(d < 0){
                                    return scale_y(0)
                                }
                                return 49.5*height/100
                        }
                    })
                    // width 根據 x軸 不然沒辦法顯示正確資料
                    .attr('width', (d, i, array) => {
                        const is_first =  i === 0
                        const is_latest = i === array.length - 1
                        return (is_first || is_latest) ? scale_data(0.5) : scale_data(1)
                    })
                    .attr('height', (d) => {
                        switch (this.diagram_shape) {
                            case 'down_to_up':
                                return judge_value_valid(d) ? height - scale_y(d) : height - scale_y(this.y_axis.min_value + 0.25)
                            case 'up_to_down':
                                return judge_value_valid(d) ? scale_y(d) : scale_y(this.y_axis.max_value - 0.25)
                            case 'from_center':
                                if (d > 0) {
                                    return scale_y(0) - scale_y(d)
                                } else if( d < 0){
                                    return scale_y(d) - scale_y(0)
                                }
                                return height/50
                        }
                    })

                // draw line
                const line = d3.line()
                    .defined((d) => judge_value_valid(d))
                    .x((d, i) => margin.left + scale_data(i) )
                    .y((d) => scale_y(d))

                svg.append('path')
                    // .attr('class', 'line')
                    .attr('d', line(this.choosed_time_series_data))
                    .attr('stroke', 'red')
                    .attr('stroke-width', 3)
                    .attr('fill', 'None')

                const CROSS_ENLARGE_SIZE = 4
                svg.selectAll('cross')
                    .data(this.choosed_time_series_data)
                    .enter()
                    .append('path')
                    .attr('d', d3
                        .symbol()
                        .size(scale_data(1) * CROSS_ENLARGE_SIZE)
                        .type(d3.symbolCross)
                    )
                    .attr('class', 'cross')
                    .attr('fill', 'red')
                    .attr('transform', (d, i) => {
                        return `translate(${margin.left + scale_data(i)}, ${judge_value_valid(d) ? scale_y(d) : height}) rotate(45)`
                    })
                    .style('opacity', (d) => judge_value_valid(d) ? 0 : 1)

                // 滑鼠覆蓋時的資料提示

                draw_tooltip({
                    basic: {
                        svg,
                        id: this.id,
                        scale_data, 
                        scale_time,
                        scale_y,
                        height: this.height, 
                        margin: this.basic_layout_setting.margin
                    },
                    tooltip: {
                        text: this.tooltip_value_text,
                        unit: this.tooltip_value_unit,
                        no_data_text: this.$t('__chart_common.no_data')
                    },
                    data: {
                        content: this.choosed_time_series_data,
                        x_axis: this.x_axis_time_series_data,
                    }
                })
            },

            save_png(file_name) {
                const node = document.querySelector('#base-popup__capture_range')
                take_screenshot(node, file_name)
            },
		},
    }
</script>

<style lang="scss">
#qpe_line_bar_complex{
    position: relative;
    height: 30vh;
    margin: 0 auto;
    padding-left: 0.25rem;
    box-sizing: border-box;

    text {
        font-size: 15px;
    }
    
    .tooltip {
        position: absolute;
        padding: 0.125rem 0.25rem;
        background: white;
        font: 12px sans-serif;
        text-align: left;
        border-radius: 2px;
        pointer-events: none;
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.4);
        white-space: nowrap;
    }

    .vertical_text {
        -webkit-writing-mode: vertical-lr;
        writing-mode: vertical-lr;
    }

    .date_line line{
        stroke: #ece8f7;
        stroke-width: 2;
        stroke-dasharray: 7;
    }

    .date_line path{
        stroke-dasharray: 2000;
        stroke-dashoffset: 2000;
    }

    .date_line text {
        opacity: 0.85; fill: rgb(192, 192, 192);
    }

    .main_title {
        fill: rgb(97, 130, 181); 
        font-weight: bold; 
        font-family: 微軟正黑體;
    }

    .title_font_size {
        font-size: 18px;
    }

    .line {
        stroke: red;
    }
}

.svg-container {
    position: relative;
}

.capture_img {
    position: absolute;
    right: 0;
    margin: 0 2rem;
    width: 8rem;
}
</style>